import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BellRing, BellOff, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BentoCard } from "@/components/dashboard/BentoCard";
import { Switch } from "@/components/ui/switch";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const pushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const PushSettings = () => {
  const queryClient = useQueryClient();
  const { data: config } = useQuery({
    queryKey: ["push-config"],
    queryFn: async () => (await api.get("/push/config")).data,
    retry: false,
  });

  const toggleMut = useMutation({
    mutationFn: async (enabled) => {
      if (!enabled) {
        return (await api.post("/push/toggle", { enabled: false })).data;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("permission-denied");
      }
      const registration = await navigator.serviceWorker.ready;
      let sub = await registration.pushManager.getSubscription();
      if (!sub) {
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.public_key),
        });
      }
      const json = sub.toJSON();
      return (await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys })).data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["push-config"] });
      toast.success(data.enabled ? "Push notifications on" : "Push notifications off");
    },
    onError: (err) => {
      if (err?.message === "permission-denied") {
        toast.error("Notifications are blocked by your browser — allow them in site settings and retry.");
      } else {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Could not update notifications");
      }
      queryClient.invalidateQueries({ queryKey: ["push-config"] });
    },
  });

  const testMut = useMutation({
    mutationFn: async () => (await api.post("/push/test")).data,
    onSuccess: () => toast.success("Test notification sent — check your device"),
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Test push failed");
    },
  });

  if (!config) return null;
  const supported = pushSupported();
  const enabled = config.enabled && config.has_subscription;

  return (
    <BentoCard data-testid="push-settings-card" className="!p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {enabled ? (
            <BellRing size={20} className="text-red-600 mt-0.5" />
          ) : (
            <BellOff size={20} className="text-zinc-400 mt-0.5" />
          )}
          <div>
            <div className="font-display font-bold">Push notifications</div>
            <p className="text-sm text-zinc-600 mt-1 max-w-lg">
              Get an instant notification on this device when a new listing scoring{" "}
              <strong>{config.score_threshold}+</strong> matches one of your saved searches —
              address, amount owed, and score included.
            </p>
            {!supported ? (
              <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1" data-testid="push-unsupported">
                <Smartphone size={12} /> Not supported in this browser. On iPhone, install the
                app to your home screen first (iOS 16.4+).
              </p>
            ) : null}
            {enabled ? (
              <button
                data-testid="push-test-btn"
                onClick={() => testMut.mutate()}
                disabled={testMut.isPending}
                className="mt-3 text-xs font-semibold text-red-600 underline hover:no-underline"
              >
                {testMut.isPending ? "Sending…" : "Send a test notification"}
              </button>
            ) : null}
          </div>
        </div>
        <Switch
          data-testid="push-toggle"
          checked={enabled}
          disabled={!supported || toggleMut.isPending}
          onCheckedChange={(checked) => toggleMut.mutate(checked)}
        />
      </div>
    </BentoCard>
  );
};
